import { type ReactNode } from "react";
import { NEXTGEN_MAX_CONTENT_WIDTH } from "~/lib/layout";

// Minimum width of the right panel. The panel grows to fill whatever margin is left of
// NEXTGEN_MAX_CONTENT_WIDTH on wide viewports, rather than leaving it blank.
const PANEL_MIN_WIDTH_PX = 480;

// The grid always has a leading/trailing 1fr gutter and a `list` column capped at
// NEXTGEN_MAX_CONTENT_WIDTH; only the trailing gutter's sizing function changes. Closed, it's a
// bare 1fr so it mirrors the leading gutter and the list stays centered. Open, it becomes the
// `panel` column with a floor so the panel claims that space instead of leaving it blank.
const GRID_TEMPLATE_COLUMNS_CLOSED = `1fr minmax(0, ${NEXTGEN_MAX_CONTENT_WIDTH}px) 1fr`;
const GRID_TEMPLATE_COLUMNS_OPEN = `1fr minmax(0, ${NEXTGEN_MAX_CONTENT_WIDTH}px) minmax(${PANEL_MIN_WIDTH_PX}px, 1fr)`;
const GRID_TEMPLATE_AREAS = '"gutter list panel"';

interface SidePanelLayoutProps {
  // Content for the right panel. Pass `null` to keep it closed — the list stays centered and no
  // panel column is rendered.
  panel: ReactNode;
  children: ReactNode;
}

export function SidePanelLayout({ panel, children }: SidePanelLayoutProps) {
  return (
    <div
      className="grid gap-6 transition-[grid-template-columns] duration-300"
      style={{
        gridTemplateColumns: panel ? GRID_TEMPLATE_COLUMNS_OPEN : GRID_TEMPLATE_COLUMNS_CLOSED,
        gridTemplateAreas: GRID_TEMPLATE_AREAS,
      }}
    >
      <div className="flex min-w-0 flex-col gap-4" style={{ gridArea: "list" }}>
        {children}
      </div>
      {panel && (
        <div className="min-w-0 overflow-hidden" style={{ gridArea: "panel" }}>
          {panel}
        </div>
      )}
    </div>
  );
}
